import React, { useEffect, useRef, useState } from "react";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, CreditCard, ShieldCheck, HardDrive, Cloud, Radio, Bell, MessageCircle, Rabbit } from "lucide-react";
import { BunnyCleanup } from "@/components/admin/BunnyCleanup";
import { RevenueCatCard } from "@/components/admin/RevenueCatCard";
import { ImageVariantsCard } from "@/components/admin/ImageVariantsCard";

export const IntegrationsPanel = () => {
  const [it, setIt] = useState(null);
  const [emailKey, setEmailKey] = useState("");
  const [s3Secret, setS3Secret] = useState("");
  const [r2Secret, setR2Secret] = useState("");
  const [streamToken, setStreamToken] = useState("");
  const [bunnyApiKey, setBunnyApiKey] = useState("");
  const [bunnyReadKey, setBunnyReadKey] = useState("");
  const [testingBunny, setTestingBunny] = useState(false);
  const [bunnyTestResult, setBunnyTestResult] = useState(null);
  const [fbServiceAccount, setFbServiceAccount] = useState("");
  const [waAccessToken, setWaAccessToken] = useState("");
  const [waTestPhone, setWaTestPhone] = useState("");
  const [waTesting, setWaTesting] = useState(false);
  const [waTestResult, setWaTestResult] = useState(null);
  const [rcSecret, setRcSecret] = useState("");
  const [stripeTestSecret, setStripeTestSecret] = useState("");
  const [stripeLiveSecret, setStripeLiveSecret] = useState("");
  const [stripeTestWebhook, setStripeTestWebhook] = useState("");
  const [stripeLiveWebhook, setStripeLiveWebhook] = useState("");
  const [testingStripe, setTestingStripe] = useState(false);
  const [stripeTestResult, setStripeTestResult] = useState(null);
  const [testingS3, setTestingS3] = useState(false);
  const [s3TestResult, setS3TestResult] = useState(null);
  const [testingR2, setTestingR2] = useState(false);
  const [r2TestResult, setR2TestResult] = useState(null);
  const [testingStream, setTestingStream] = useState(false);
  const [streamTestResult, setStreamTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [migStatus, setMigStatus] = useState(null);
  const [r2MigStatus, setR2MigStatus] = useState(null);
  const pollRef = useRef(null);
  const r2PollRef = useRef(null);

  const fetchMigStatus = async () => {
    try { const { data } = await api.get("/admin/integrations/storage/migrate/status"); setMigStatus(data); return data; } catch { return null; }
  };
  const fetchR2MigStatus = async () => {
    try { const { data } = await api.get("/admin/integrations/storage/migrate-to-r2/status"); setR2MigStatus(data); return data; } catch { return null; }
  };

  const load = () => api.get("/admin/integrations").then(({ data }) => setIt(data.integrations || {})).catch(() => {});
  useEffect(() => { load(); fetchMigStatus(); fetchR2MigStatus(); return () => { clearInterval(pollRef.current); clearInterval(r2PollRef.current); }; }, []);
  if (!it) return <AdminSkeleton />;

  const email = it.email || {};
  const stripe = it.stripe || {};
  const moderation = it.moderation || {};
  const storage = it.storage || {};
  const r2 = it.storage_r2 || {};
  const stream = it.stream || {};
  const bunny = it.bunny || {};
  const bunnyWebhookUrl = `${process.env.REACT_APP_BACKEND_URL || ""}/api/webhooks/bunny`;
  const firebase = it.firebase || {};
  const whatsapp = it.whatsapp || {};
  const iap = it.iap || {};
  const upd = (section, patch) => setIt({ ...it, [section]: { ...(it[section] || {}), ...patch } });

  const r2Endpoint = (r2.endpoint || "").trim() || (r2.account_id ? `https://${r2.account_id}.r2.cloudflarestorage.com` : "");

  const persist = async (showToast) => {
    const payload = {
      email: { provider: "resend", from_email: email.from_email || "", from_name: email.from_name || "", enabled: !!email.enabled },
      stripe: {
        mode: stripe.mode || "test",
        enabled: !!stripe.enabled,
        test_publishable_key: (stripe.test_publishable_key || "").trim(),
        live_publishable_key: (stripe.live_publishable_key || "").trim(),
      },
      moderation: { enabled: !!moderation.enabled, provider: moderation.provider || "claude" },
      storage: {
        provider: "s3",
        access_key_id: storage.access_key_id || "",
        region: storage.region || "",
        bucket: storage.bucket || "",
        enabled: !!storage.enabled,
      },
      storage_r2: {
        account_id: (r2.account_id || "").trim(),
        access_key_id: (r2.access_key_id || "").trim(),
        bucket: (r2.bucket || "").trim(),
        endpoint: (r2.endpoint || "").trim(),
        public_url: (r2.public_url || "").trim(),
        enabled: !!r2.enabled,
      },
      stream: {
        account_id: (stream.account_id || "").trim(),
        customer_code: (stream.customer_code || "").trim(),
        enabled: !!stream.enabled,
      },
      bunny: {
        library_id: String(bunny.library_id || "").trim(),
        cdn_hostname: (bunny.cdn_hostname || "").trim(),
        enabled: !!bunny.enabled,
      },
      firebase: {
        project_id: (firebase.project_id || "").trim(),
        enabled: firebase.enabled !== false,
      },
      whatsapp: {
        phone_number_id: (whatsapp.phone_number_id || "").trim(),
        template_name: (whatsapp.template_name || "").trim(),
        template_language: (whatsapp.template_language || "fr").trim(),
        reminder_template_name: (whatsapp.reminder_template_name || "").trim(),
        graph_version: (whatsapp.graph_version || "v22.0").trim(),
        enabled: whatsapp.enabled !== false,
      },
      iap: {
        enabled: !!iap.enabled,
        sandbox: iap.sandbox !== false,
        alert_failures: iap.alert_failures !== false,
        rc_public_key_ios: (iap.rc_public_key_ios || "").trim(),
        rc_public_key_android: (iap.rc_public_key_android || "").trim(),
        products: (iap.products || []).filter((p) => (p.product_id || "").trim()).map((p) => ({
          product_id: p.product_id.trim(), google_product_id: (p.google_product_id || "").trim() || null,
          kind: p.kind === "verified_yearly" ? "verified_yearly" : "adcredit", amount: Number(p.amount) || 0, enabled: p.enabled !== false,
        })),
      },
    };
    if (rcSecret.trim()) payload.iap.rc_secret_key = rcSecret.trim();
    if (emailKey.trim()) payload.email.api_key = emailKey.trim();
    if (s3Secret.trim()) payload.storage.secret_access_key = s3Secret.trim();
    if (r2Secret.trim()) payload.storage_r2.secret_access_key = r2Secret.trim();
    if (streamToken.trim()) payload.stream.api_token = streamToken.trim();
    if (bunnyApiKey.trim()) payload.bunny.api_key = bunnyApiKey.trim();
    if (bunnyReadKey.trim()) payload.bunny.read_only_key = bunnyReadKey.trim();
    if (fbServiceAccount.trim()) payload.firebase.service_account_json = fbServiceAccount.trim();
    if (waAccessToken.trim()) payload.whatsapp.access_token = waAccessToken.trim();
    if (stripeTestSecret.trim()) payload.stripe.test_secret_key = stripeTestSecret.trim();
    if (stripeLiveSecret.trim()) payload.stripe.live_secret_key = stripeLiveSecret.trim();
    if (stripeTestWebhook.trim()) payload.stripe.test_webhook_secret = stripeTestWebhook.trim();
    if (stripeLiveWebhook.trim()) payload.stripe.live_webhook_secret = stripeLiveWebhook.trim();
    const { data } = await api.patch("/admin/integrations", payload);
    setIt(data.integrations); setEmailKey(""); setS3Secret(""); setR2Secret(""); setStreamToken(""); setFbServiceAccount(""); setWaAccessToken("");
    setBunnyApiKey(""); setBunnyReadKey(""); setRcSecret("");
    setStripeTestSecret(""); setStripeLiveSecret(""); setStripeTestWebhook(""); setStripeLiveWebhook("");
    if (showToast) toast.success("Intégrations enregistrées");
    return data.integrations;
  };

  const save = async () => {
    setSaving(true);
    try { await persist(true); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setSaving(false); }
  };

  const testWhatsApp = async () => {
    if (!waTestPhone.trim()) { toast.error("Entre un numéro de test (format international, ex +243812345678)"); return; }
    setWaTesting(true); setWaTestResult(null);
    try {
      await persist(false); // save the current keys first so the test reads fresh config
      const { data } = await api.post("/admin/integrations/whatsapp/test", { phone: waTestPhone.trim() });
      setWaTestResult(data);
      if (data.ok) toast.success(data.message || "Code de test envoyé");
      else toast.error(data.error || "Échec de l'envoi");
    } catch (e) {
      setWaTestResult({ ok: false, error: e?.friendlyMessage || "Erreur" });
      toast.error(e?.friendlyMessage || "Erreur");
    } finally { setWaTesting(false); }
  };

  const testS3 = async () => {
    setTestingS3(true); setS3TestResult(null);
    try {
      await persist(false); // save form first so the test reads fresh config
      const { data } = await api.post("/admin/integrations/storage/test");
      setS3TestResult(data);
      if (data.ok) toast.success("Connexion S3 réussie ✓" + (data.cors_ok ? " (CORS configuré)" : ""));
      else toast.error("Échec du test S3");
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setTestingS3(false); }
  };

  const testR2 = async () => {
    setTestingR2(true); setR2TestResult(null);
    try {
      await persist(false); // save form first so the test reads fresh config
      const { data } = await api.post("/admin/integrations/storage/r2/test");
      setR2TestResult(data);
      if (data.ok) toast.success("Connexion Cloudflare R2 réussie ✓" + (data.cors_ok ? " (CORS configuré)" : ""));
      else toast.error("Échec du test R2");
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setTestingR2(false); }
  };

  const testStream = async () => {
    setTestingStream(true); setStreamTestResult(null);
    try {
      await persist(false); // save form first so the test reads fresh config
      const { data } = await api.post("/admin/integrations/stream/test");
      setStreamTestResult(data);
      if (data.ok) toast.success("Connexion Cloudflare Stream réussie ✓");
      else toast.error("Échec du test Stream");
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setTestingStream(false); }
  };

  const testStripe = async () => {
    setTestingStripe(true); setStripeTestResult(null);
    try {
      await persist(false); // save form first so the test reads fresh config
      const { data } = await api.post("/admin/integrations/stripe/test");
      setStripeTestResult(data);
      if (data.ok) toast.success(`Connexion Stripe réussie ✓ (mode ${data.mode})`);
      else toast.error(data.error || "Échec du test Stripe");
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setTestingStripe(false); }
  };

  const testBunny = async () => {
    setTestingBunny(true); setBunnyTestResult(null);
    try {
      await persist(false); // save form first so the test reads fresh config
      const { data } = await api.post("/admin/integrations/bunny/test");
      setBunnyTestResult(data);
      if (data.ok) toast.success("Connexion Bunny Stream réussie ✓");
      else toast.error(data.error || "Échec du test Bunny");
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setTestingBunny(false); }
  };

  const iamPolicy = (bucket) => JSON.stringify({
    Version: "2012-10-17",
    Statement: [
      { Sid: "YalolaObjects", Effect: "Allow", Action: ["s3:PutObject", "s3:GetObject", "s3:DeleteObject"], Resource: `arn:aws:s3:::${bucket || "VOTRE-BUCKET"}/*` },
      { Sid: "YalolaBucketCors", Effect: "Allow", Action: ["s3:PutBucketCORS", "s3:GetBucketCORS"], Resource: `arn:aws:s3:::${bucket || "VOTRE-BUCKET"}` },
    ],
  }, null, 2);

  const startMigration = async () => {
    try {
      await api.post("/admin/integrations/storage/migrate");
      clearInterval(pollRef.current);
      const tick = async () => {
        const s = await fetchMigStatus();
        if (s && !s.running) {
          clearInterval(pollRef.current); pollRef.current = null;
          if ((s.failed || 0) === 0) toast.success(`Migration terminée ✓ (${s.done} fichier(s) migré(s))`);
          else toast.warning(`Migration terminée avec ${s.failed} échec(s)`);
        }
      };
      pollRef.current = setInterval(tick, 2000);
      tick();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const startR2Migration = async () => {
    try {
      await api.post("/admin/integrations/storage/migrate-to-r2");
      clearInterval(r2PollRef.current);
      const tick = async () => {
        const s = await fetchR2MigStatus();
        if (s && !s.running) {
          clearInterval(r2PollRef.current); r2PollRef.current = null;
          if ((s.failed || 0) === 0) toast.success(`Migration R2 terminée ✓ (${s.done} fichier(s) migré(s))`);
          else toast.warning(`Migration R2 terminée avec ${s.failed} échec(s)`);
        }
      };
      r2PollRef.current = setInterval(tick, 2000);
      tick();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-integrations-panel">
      {/* Email */}
      <div className="yalola-card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Mail size={18} /></div>
          <div className="font-bold font-display text-lg">Email</div>
          <span className={`chip text-[10px] ml-auto ${email.api_key_set ? "chip-primary" : ""}`}>{email.api_key_set ? "Clé configurée" : "Non configuré"}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Fournisseur</Label>
            <div className="h-10 rounded-lg border px-3 flex items-center text-sm font-medium" style={{ background: "var(--yalola-surface-alt)", borderColor: "var(--yalola-border)" }} data-testid="int-email-provider">
              Resend
            </div>
            <p className="text-[11px] mt-1">
              <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="underline" style={{ color: "var(--yalola-primary)" }} data-testid="int-resend-link">
                Obtenir une clé gratuite sur resend.com →
              </a>
            </p>
          </div>
          <div><Label>Email expéditeur</Label><Input value={email.from_email || ""} onChange={(e) => upd("email", { from_email: e.target.value })} placeholder="noreply@yalola.app" data-testid="int-email-from" /></div>
          <div><Label>Nom expéditeur</Label><Input value={email.from_name || ""} onChange={(e) => upd("email", { from_name: e.target.value })} placeholder="Yalola" data-testid="int-email-fromname" /></div>
          <div>
            <Label>Clé API Resend {email.api_key_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({email.api_key_hint})</span>}</Label>
            <Input type="password" value={emailKey} onChange={(e) => setEmailKey(e.target.value)} placeholder={email.api_key_set ? "•••••••• (laisser vide pour garder)" : "re_..."} data-testid="int-email-key" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl px-4 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
          <span className="text-sm">Activer l'envoi d'emails</span>
          <Switch checked={!!email.enabled} onCheckedChange={(v) => upd("email", { enabled: v })} data-testid="int-email-enabled" />
        </div>
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Colle ta clé API email ici — elle est stockée de façon sécurisée et masquée.</p>
      </div>

      {/* Stripe */}
      <div className="yalola-card p-5 space-y-3" data-testid="int-stripe-card">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--yalola-secondary-soft)", color: "var(--yalola-secondary)" }}><CreditCard size={18} /></div>
          <div className="font-bold font-display text-lg">Paiements (Stripe)</div>
          <span className={`chip text-[10px] ml-auto ${stripe.enabled && stripe.verified ? "chip-primary" : ""}`} data-testid="int-stripe-status">
            {stripe.enabled && stripe.verified ? `Actif ✓ (${stripe.mode || "test"})` : stripe.enabled ? "À tester" : "Non configuré"}
          </span>
        </div>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Colle tes clés Stripe pour tester les <b>dons</b>, <b>offrandes</b> et la <b>vérification de compte</b>. Le mode <b>Test</b> fonctionne déjà avec la clé de l'environnement — colle tes clés <b>Live</b> quand tu passes en production.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Mode actif</Label>
            <select className="w-full h-10 rounded-lg border bg-white px-3" value={stripe.mode || "test"} onChange={(e) => upd("stripe", { mode: e.target.value })} data-testid="int-stripe-mode">
              <option value="test">Test (sandbox)</option>
              <option value="live">Live (production)</option>
            </select>
          </div>
          <div className="flex items-center justify-between rounded-xl px-4 py-2 self-end" style={{ background: "var(--yalola-surface-alt)" }}>
            <span className="text-sm">Activer Stripe</span>
            <Switch checked={!!stripe.enabled} onCheckedChange={(v) => upd("stripe", { enabled: v })} data-testid="int-stripe-enabled" />
          </div>
        </div>

        {/* Test keys */}
        <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="text-sm font-semibold flex items-center gap-2">Clés TEST <span className="chip text-[10px]">sandbox</span></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Clé publique test (pk_test_…)</Label><Input value={stripe.test_publishable_key || ""} onChange={(e) => upd("stripe", { test_publishable_key: e.target.value })} placeholder="pk_test_..." data-testid="int-stripe-test-pub" /></div>
            <div>
              <Label>Clé secrète test {stripe.test_secret_key_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({stripe.test_secret_key_hint})</span>}</Label>
              <Input type="password" value={stripeTestSecret} onChange={(e) => setStripeTestSecret(e.target.value)} placeholder={stripe.test_secret_key_set ? "•••••••• (laisser vide pour garder)" : "sk_test_... (défaut : environnement)"} data-testid="int-stripe-test-secret" />
            </div>
            <div className="sm:col-span-2">
              <Label>Secret webhook test {stripe.test_webhook_secret_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({stripe.test_webhook_secret_hint})</span>}</Label>
              <Input type="password" value={stripeTestWebhook} onChange={(e) => setStripeTestWebhook(e.target.value)} placeholder={stripe.test_webhook_secret_set ? "•••••••• (laisser vide pour garder)" : "whsec_..."} data-testid="int-stripe-test-webhook" />
            </div>
          </div>
        </div>

        {/* Live keys */}
        <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="text-sm font-semibold flex items-center gap-2">Clés LIVE <span className="chip text-[10px]">production</span></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Clé publique live (pk_live_…)</Label><Input value={stripe.live_publishable_key || ""} onChange={(e) => upd("stripe", { live_publishable_key: e.target.value })} placeholder="pk_live_..." data-testid="int-stripe-live-pub" /></div>
            <div>
              <Label>Clé secrète live {stripe.live_secret_key_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({stripe.live_secret_key_hint})</span>}</Label>
              <Input type="password" value={stripeLiveSecret} onChange={(e) => setStripeLiveSecret(e.target.value)} placeholder={stripe.live_secret_key_set ? "•••••••• (laisser vide pour garder)" : "sk_live_..."} data-testid="int-stripe-live-secret" />
            </div>
            <div className="sm:col-span-2">
              <Label>Secret webhook live {stripe.live_webhook_secret_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({stripe.live_webhook_secret_hint})</span>}</Label>
              <Input type="password" value={stripeLiveWebhook} onChange={(e) => setStripeLiveWebhook(e.target.value)} placeholder={stripe.live_webhook_secret_set ? "•••••••• (laisser vide pour garder)" : "whsec_..."} data-testid="int-stripe-live-webhook" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={testStripe} disabled={testingStripe} data-testid="int-stripe-test">
            {testingStripe ? "Test en cours..." : "Tester la connexion Stripe"}
          </Button>
          {stripeTestResult && (
            <span className={`text-xs font-semibold ${stripeTestResult.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="int-stripe-test-result">
              {stripeTestResult.ok ? `Connexion OK ✓ · mode ${stripeTestResult.mode}${stripeTestResult.country ? " · " + stripeTestResult.country : ""}` : stripeTestResult.error}
            </span>
          )}
        </div>
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
          Les clés secrètes sont stockées de façon sécurisée et jamais réaffichées. Le paiement s'ouvre dans une fenêtre popup pour ne pas quitter le direct.
          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="underline ml-1" style={{ color: "var(--yalola-primary)" }}>Ouvrir mes clés Stripe →</a>
        </p>
      </div>

      {/* Moderation */}
      <div className="yalola-card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--yalola-accent-soft)", color: "#8A5A12" }}><ShieldCheck size={18} /></div>
          <div className="font-bold font-display text-lg">Modération IA</div>
        </div>
        <div className="flex items-center justify-between rounded-xl px-4 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
          <span className="text-sm">Analyse automatique des images (détection de nudité)</span>
          <Switch checked={!!moderation.enabled} onCheckedChange={(v) => upd("moderation", { enabled: v })} data-testid="int-moderation-enabled" />
        </div>
      </div>

      {/* Cloudflare R2 (primary storage) */}
      <div className="yalola-card p-5 space-y-3" data-testid="int-r2-card">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "#FEF3E2", color: "#F6821F" }}><Cloud size={18} /></div>
          <div className="font-bold font-display text-lg">Stockage Cloudflare R2</div>
          <span className={`chip text-[10px] ml-auto ${r2.enabled && r2.verified ? "chip-primary" : ""}`} data-testid="int-r2-status">
            {r2.enabled && r2.verified ? "Actif ✓" : r2.enabled && r2.secret_access_key_set ? "À tester" : "Non configuré"}
          </span>
        </div>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Stockage d'objets S3-compatible, <b>sans frais de sortie</b> et servi par le CDN mondial de Cloudflare. Recommandé pour héberger images, vidéos et audios.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Account ID</Label><Input value={r2.account_id || ""} onChange={(e) => upd("storage_r2", { account_id: e.target.value })} placeholder="abc123..." data-testid="int-r2-account" /></div>
          <div><Label>Bucket</Label><Input value={r2.bucket || ""} onChange={(e) => upd("storage_r2", { bucket: e.target.value })} placeholder="yalola-media" data-testid="int-r2-bucket" /></div>
          <div><Label>Access Key ID</Label><Input value={r2.access_key_id || ""} onChange={(e) => upd("storage_r2", { access_key_id: e.target.value })} placeholder="Access Key ID R2" data-testid="int-r2-access-key" /></div>
          <div>
            <Label>Secret Access Key {r2.secret_access_key_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({r2.secret_access_key_hint})</span>}</Label>
            <Input type="password" value={r2Secret} onChange={(e) => setR2Secret(e.target.value)} placeholder={r2.secret_access_key_set ? "•••••••• (laisser vide pour garder)" : "Secret R2"} data-testid="int-r2-secret" />
          </div>
          <div>
            <Label>Endpoint S3 <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>(auto si Account ID)</span></Label>
            <Input value={r2.endpoint || ""} onChange={(e) => upd("storage_r2", { endpoint: e.target.value })} placeholder={r2Endpoint || "https://<ACCOUNT_ID>.r2.cloudflarestorage.com"} data-testid="int-r2-endpoint" />
          </div>
          <div>
            <Label>URL publique <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>(r2.dev ou domaine)</span></Label>
            <Input value={r2.public_url || ""} onChange={(e) => upd("storage_r2", { public_url: e.target.value })} placeholder="https://media.yalola.app" data-testid="int-r2-public-url" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl px-4 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
          <span className="text-sm">Utiliser Cloudflare R2 pour les nouveaux fichiers</span>
          <Switch checked={!!r2.enabled} onCheckedChange={(v) => upd("storage_r2", { enabled: v })} data-testid="int-r2-enabled" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={testR2} disabled={testingR2} data-testid="int-r2-test">
            {testingR2 ? "Test en cours..." : "Tester la connexion"}
          </Button>
          {r2TestResult && (
            <span className={`text-xs font-semibold ${r2TestResult.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="int-r2-test-result">
              {r2TestResult.ok ? `Connexion OK ✓${r2TestResult.cors_ok ? " · CORS configuré ✓" : " · CORS à configurer manuellement"}` : r2TestResult.error}
            </span>
          )}
        </div>
        {r2.verified && (
          <div className="rounded-xl px-4 py-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="int-r2-migrate-section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <div className="font-semibold">Migration des anciens fichiers vers R2</div>
                <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Copie tous les fichiers existants (S3 ou stockage intégré) vers votre bucket R2 — ils seront ensuite servis par Cloudflare.</div>
              </div>
              <Button variant="outline" size="sm" className="rounded-full" onClick={startR2Migration} disabled={!!r2MigStatus?.running} data-testid="int-r2-migrate-btn">
                {r2MigStatus?.running ? "Migration en cours..." : "Migrer les fichiers vers R2"}
              </Button>
            </div>
            {r2MigStatus && (r2MigStatus.running || r2MigStatus.finished_at) && (
              <div className="text-xs font-medium" data-testid="int-r2-migrate-status">
                {r2MigStatus.running
                  ? `En cours : ${r2MigStatus.done + r2MigStatus.skipped + r2MigStatus.failed} / ${r2MigStatus.total} fichiers...`
                  : `Terminé : ${r2MigStatus.done} migré(s) · ${r2MigStatus.skipped} déjà sur R2 · ${r2MigStatus.failed} échec(s) (total ${r2MigStatus.total})`}
              </div>
            )}
            {r2MigStatus?.running && r2MigStatus.total > 0 && (
              <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.round(((r2MigStatus.done + r2MigStatus.skipped + r2MigStatus.failed) / r2MigStatus.total) * 100)}%`, background: "var(--yalola-primary)" }} />
              </div>
            )}
          </div>
        )}
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
          Enregistrez d'abord vos clés, puis testez.
          <a href="https://dash.cloudflare.com/?to=/:account/r2" target="_blank" rel="noreferrer" className="underline ml-1" style={{ color: "var(--yalola-primary)" }}>Ouvrir Cloudflare R2 →</a>
        </p>
      </div>

      <ImageVariantsCard />

      {/* Cloudflare Stream (live) */}
      <div className="yalola-card p-5 space-y-3" data-testid="int-stream-card">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "#FEF3E2", color: "#F6821F" }}><Radio size={18} /></div>
          <div className="font-bold font-display text-lg">Directs Cloudflare Stream</div>
          <span className={`chip text-[10px] ml-auto ${stream.enabled && stream.verified ? "chip-primary" : ""}`} data-testid="int-stream-status">
            {stream.enabled && stream.verified ? "Actif ✓" : stream.enabled && stream.api_token_set ? "À tester" : "Non configuré"}
          </span>
        </div>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Vrais directs OBS/RTMP joués dans Yalola (lecteur HLS), avec replay automatique. Remplace à terme les liens YouTube/Facebook.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Account ID</Label><Input value={stream.account_id || ""} onChange={(e) => upd("stream", { account_id: e.target.value })} placeholder="abc123..." data-testid="int-stream-account" /></div>
          <div><Label>Customer code <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>(customer-XXXX)</span></Label><Input value={stream.customer_code || ""} onChange={(e) => upd("stream", { customer_code: e.target.value })} placeholder="ex. abcdef1234567890" data-testid="int-stream-customer" /></div>
          <div>
            <Label>API Token (Stream:Edit) {stream.api_token_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({stream.api_token_hint})</span>}</Label>
            <Input type="password" value={streamToken} onChange={(e) => setStreamToken(e.target.value)} placeholder={stream.api_token_set ? "•••••••• (laisser vide pour garder)" : "Token Cloudflare Stream"} data-testid="int-stream-token" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl px-4 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
          <span className="text-sm">Activer les directs Cloudflare Stream</span>
          <Switch checked={!!stream.enabled} onCheckedChange={(v) => upd("stream", { enabled: v })} data-testid="int-stream-enabled" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={testStream} disabled={testingStream} data-testid="int-stream-test">
            {testingStream ? "Test en cours..." : "Tester la connexion"}
          </Button>
          {streamTestResult && (
            <span className={`text-xs font-semibold ${streamTestResult.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="int-stream-test-result">
              {streamTestResult.ok ? "Connexion OK ✓" : streamTestResult.error}
            </span>
          )}
        </div>
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
          Abonne-toi à Stream puis crée un token « Stream:Edit ».
          <a href="https://dash.cloudflare.com/?to=/:account/stream" target="_blank" rel="noreferrer" className="underline ml-1" style={{ color: "var(--yalola-primary)" }}>Ouvrir Cloudflare Stream →</a>
        </p>
      </div>

      {/* Bunny Stream (VOD hosting — replays, reels, feed videos) */}
      <div className="yalola-card p-5 space-y-3" data-testid="int-bunny-card">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "#EAF7EF", color: "#0F9D58" }}><Rabbit size={18} /></div>
          <div className="font-bold font-display text-lg">Vidéos VOD — Bunny Stream</div>
          <span className={`chip text-[10px] ml-auto ${bunny.enabled && bunny.verified ? "chip-primary" : ""}`} data-testid="int-bunny-status">
            {bunny.enabled && bunny.verified ? "Actif ✓" : bunny.enabled && bunny.api_key_set ? "À tester" : "Non configuré"}
          </span>
        </div>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Héberge les <strong>rediffusions, reels et vidéos du fil</strong> en HLS adaptatif, bien moins cher (au Go). Quand c'est <strong>activé</strong>, les nouvelles vidéos vont sur Bunny ; le <strong>direct</strong> reste sur Cloudflare Stream. Les anciennes vidéos continuent de se lire normalement.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Library ID</Label><Input value={bunny.library_id || ""} onChange={(e) => upd("bunny", { library_id: e.target.value })} placeholder="ex. 732013" data-testid="int-bunny-library" /></div>
          <div><Label>CDN Hostname</Label><Input value={bunny.cdn_hostname || ""} onChange={(e) => upd("bunny", { cdn_hostname: e.target.value })} placeholder="vz-xxxxxxxx.b-cdn.net" data-testid="int-bunny-cdn" /></div>
          <div>
            <Label>Stream API Key {bunny.api_key_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({bunny.api_key_hint})</span>}</Label>
            <Input type="password" value={bunnyApiKey} onChange={(e) => setBunnyApiKey(e.target.value)} placeholder={bunny.api_key_set ? "•••••••• (laisser vide pour garder)" : "Clé API de la vidéothèque"} data-testid="int-bunny-key" />
          </div>
          <div>
            <Label>Read-Only Key <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>(facultatif)</span> {bunny.read_only_key_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({bunny.read_only_key_hint})</span>}</Label>
            <Input type="password" value={bunnyReadKey} onChange={(e) => setBunnyReadKey(e.target.value)} placeholder={bunny.read_only_key_set ? "•••••••• (laisser vide pour garder)" : "Clé API en lecture seule"} data-testid="int-bunny-readkey" />
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl px-4 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
          <span className="text-sm">Utiliser Bunny pour les nouvelles vidéos VOD</span>
          <Switch checked={!!bunny.enabled} onCheckedChange={(v) => upd("bunny", { enabled: v })} data-testid="int-bunny-enabled" />
        </div>
        <div className="rounded-xl px-4 py-3 space-y-1.5" style={{ background: "var(--yalola-surface-alt)" }} data-testid="int-bunny-webhook-block">
          <Label>URL du webhook Bunny <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>(pour arrêter le sondage)</span></Label>
          <div className="flex items-center gap-2">
            <Input readOnly value={bunnyWebhookUrl} className="font-mono text-xs" data-testid="int-bunny-webhook-url" onFocus={(e) => e.target.select()} />
            <Button
              type="button"
              variant="outline"
              className="rounded-full shrink-0"
              data-testid="int-bunny-webhook-copy"
              onClick={async () => {
                try { await navigator.clipboard.writeText(bunnyWebhookUrl); toast.success("URL copiée ✓"); }
                catch { toast.error("Copie impossible — copie l'URL manuellement"); }
              }}
            >
              Copier
            </Button>
          </div>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
            Colle cette URL dans Bunny → ta vidéothèque → onglet <strong>Webhook</strong>. Bunny préviendra Yalola dès qu'une vidéo est encodée → la rediffusion/reel passe en HLS <strong>instantanément</strong> (plus besoin d'attendre le sondage).
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={testBunny} disabled={testingBunny} data-testid="int-bunny-test">
            {testingBunny ? "Test en cours..." : "Tester la connexion"}
          </Button>
          {bunnyTestResult && (
            <span className={`text-xs font-semibold ${bunnyTestResult.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="int-bunny-test-result">
              {bunnyTestResult.ok ? "Connexion OK ✓" : bunnyTestResult.error}
            </span>
          )}
        </div>
        <BunnyCleanup />
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
          Crée une vidéothèque sur dash.bunny.net → onglet API pour récupérer Library ID, Stream API Key et CDN Hostname.
          <a href="https://dash.bunny.net/stream" target="_blank" rel="noreferrer" className="underline ml-1" style={{ color: "var(--yalola-primary)" }}>Ouvrir Bunny Stream →</a>
        </p>
      </div>

      {/* AWS S3 (fallback) */}
      <div className="yalola-card p-5 space-y-3" data-testid="int-storage-card">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><HardDrive size={18} /></div>
          <div className="font-bold font-display text-lg">Stockage AWS S3 <span className="text-xs font-normal" style={{ color: "var(--yalola-text-muted)" }}>(repli)</span></div>
          <span className={`chip text-[10px] ml-auto ${storage.enabled && storage.verified ? "chip-primary" : ""}`} data-testid="int-s3-status">
            {storage.enabled && storage.verified ? "Actif ✓" : storage.enabled && storage.secret_access_key_set ? "À tester" : "Non configuré"}
          </span>
        </div>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Conservé en repli. Si Cloudflare R2 est actif, il est prioritaire. Vous pouvez désactiver S3 une fois la migration R2 terminée.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Access Key ID</Label><Input value={storage.access_key_id || ""} onChange={(e) => upd("storage", { access_key_id: e.target.value })} placeholder="AKIA..." data-testid="int-s3-access-key" /></div>
          <div>
            <Label>Secret Access Key {storage.secret_access_key_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({storage.secret_access_key_hint})</span>}</Label>
            <Input type="password" value={s3Secret} onChange={(e) => setS3Secret(e.target.value)} placeholder={storage.secret_access_key_set ? "•••••••• (laisser vide pour garder)" : "Secret AWS"} data-testid="int-s3-secret" />
          </div>
          <div><Label>Région</Label><Input value={storage.region || ""} onChange={(e) => upd("storage", { region: e.target.value })} placeholder="eu-west-3 (Paris)" data-testid="int-s3-region" /></div>
          <div><Label>Bucket</Label><Input value={storage.bucket || ""} onChange={(e) => upd("storage", { bucket: e.target.value })} placeholder="yalola-media" data-testid="int-s3-bucket" /></div>
        </div>
        <div className="flex items-center justify-between rounded-xl px-4 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
          <span className="text-sm">Utiliser S3 pour les nouveaux fichiers</span>
          <Switch checked={!!storage.enabled} onCheckedChange={(v) => upd("storage", { enabled: v })} data-testid="int-s3-enabled" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={testS3} disabled={testingS3} data-testid="int-s3-test">
            {testingS3 ? "Test en cours..." : "Tester la connexion"}
          </Button>
          {s3TestResult && (
            <span className={`text-xs font-semibold ${s3TestResult.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="int-s3-test-result">
              {s3TestResult.ok ? `Connexion OK ✓${s3TestResult.cors_ok ? " · CORS configuré ✓" : " · CORS à configurer manuellement"}` : s3TestResult.error}
            </span>
          )}
        </div>
        {s3TestResult && !s3TestResult.ok && /not authorized|AccessDenied|Access Denied|Permissions IAM/i.test(s3TestResult.error || "") && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2 text-sm" data-testid="int-s3-iam-help">
            <div className="font-semibold text-amber-800">Vos clés sont valides, mais l'utilisateur IAM n'a pas les permissions S3 requises.</div>
            <ol className="list-decimal ml-5 space-y-1 text-amber-900">
              <li>Ouvrez <a href="https://console.aws.amazon.com/iam/home#/users" target="_blank" rel="noreferrer" className="underline font-medium">AWS IAM → Utilisateurs</a> et cliquez sur votre utilisateur.</li>
              <li>Onglet <b>Autorisations</b> → <b>Ajouter des autorisations</b> → <b>Créer une politique en ligne</b> → onglet <b>JSON</b>.</li>
              <li>Collez la politique ci-dessous, enregistrez, puis re-testez ici.</li>
            </ol>
            <pre className="text-[11px] bg-white border border-amber-200 rounded-lg p-3 overflow-x-auto" data-testid="int-s3-iam-policy">{iamPolicy(storage.bucket)}</pre>
            <Button variant="outline" size="sm" className="rounded-full" data-testid="int-s3-copy-policy"
              onClick={() => { navigator.clipboard.writeText(iamPolicy(storage.bucket)); toast.success("Politique copiée"); }}>
              Copier la politique
            </Button>
          </div>
        )}
        {storage.verified && (
          <div className="rounded-xl px-4 py-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="int-s3-migrate-section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <div className="font-semibold">Migration des anciens fichiers</div>
                <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Copie tous les fichiers existants vers votre bucket S3 — ils seront ensuite servis directement par Amazon.</div>
              </div>
              <Button variant="outline" size="sm" className="rounded-full" onClick={startMigration} disabled={!!migStatus?.running} data-testid="int-s3-migrate-btn">
                {migStatus?.running ? "Migration en cours..." : "Migrer les fichiers existants vers S3"}
              </Button>
            </div>
            {migStatus && (migStatus.running || migStatus.finished_at) && (
              <div className="text-xs font-medium" data-testid="int-s3-migrate-status">
                {migStatus.running
                  ? `En cours : ${migStatus.done + migStatus.skipped + migStatus.failed} / ${migStatus.total} fichiers...`
                  : `Terminé : ${migStatus.done} migré(s) · ${migStatus.skipped} déjà sur S3 · ${migStatus.failed} échec(s) (total ${migStatus.total})`}
              </div>
            )}
            {migStatus?.running && migStatus.total > 0 && (
              <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.round(((migStatus.done + migStatus.skipped + migStatus.failed) / migStatus.total) * 100)}%`, background: "var(--yalola-primary)" }} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Push natif (Firebase Cloud Messaging) */}
      <div className="yalola-card p-5 space-y-3" data-testid="int-firebase-card">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "#FFF3E0", color: "#FF8F00" }}><Bell size={18} /></div>
          <div className="font-bold font-display text-lg">Notifications push natives (Firebase)</div>
          <span className={`chip text-[10px] ml-auto ${firebase.project_id && firebase.service_account_json_set ? "chip-primary" : ""}`} data-testid="int-firebase-status">
            {firebase.project_id && firebase.service_account_json_set ? "Configuré ✓" : "Non configuré"}
          </span>
        </div>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Pour envoyer des notifications push à l'application mobile (iOS/Android). Colle l'ID du projet Firebase et la clé « compte de service » — tu peux les ajouter plus tard, l'app fonctionne sans. Une fois configuré, chaque notification de Yalola déclenchera aussi un push natif.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Firebase Project ID</Label><Input value={firebase.project_id || ""} onChange={(e) => upd("firebase", { project_id: e.target.value })} placeholder="mon-projet-firebase" data-testid="int-firebase-project" /></div>
          <div className="flex items-center justify-between rounded-xl px-4 py-2 self-end" style={{ background: "var(--yalola-surface-alt)" }}>
            <span className="text-sm">Activer le push natif</span>
            <Switch checked={firebase.enabled !== false} onCheckedChange={(v) => upd("firebase", { enabled: v })} data-testid="int-firebase-enabled" />
          </div>
        </div>
        <div>
          <Label>Compte de service (JSON) {firebase.service_account_json_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({firebase.service_account_json_hint})</span>}</Label>
          <textarea
            value={fbServiceAccount}
            onChange={(e) => setFbServiceAccount(e.target.value)}
            rows={4}
            className="mt-1 w-full rounded-xl p-3 border bg-white text-xs font-mono"
            style={{ borderColor: "var(--yalola-border)" }}
            placeholder={firebase.service_account_json_set ? "•••••••• (laisser vide pour garder)" : '{ "type": "service_account", "project_id": "...", "private_key": "...", ... }'}
            data-testid="int-firebase-service-account"
          />
        </div>
        <div className="rounded-xl px-4 py-3 text-xs space-y-1" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>
          <div className="font-semibold" style={{ color: "var(--yalola-text)" }}>À préparer côté app native (voir CAPACITOR.md) :</div>
          <div>• <b>google-services.json</b> (Android) et <b>GoogleService-Info.plist</b> (iOS) — package/bundle <b>app.yalola</b></div>
          <div>• Clé <b>APNs .p8</b> + Key ID + Team ID (Apple) à uploader dans Firebase → Cloud Messaging</div>
          <div>• Le <b>compte de service</b> (ci-dessus) sert à l'envoi côté serveur (FCM HTTP v1)</div>
        </div>
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
          Les secrets sont stockés de façon sécurisée et jamais réaffichés.
          <a href="https://console.firebase.google.com" target="_blank" rel="noreferrer" className="underline ml-1" style={{ color: "var(--yalola-primary)" }}>Ouvrir la console Firebase →</a>
        </p>
      </div>

      {/* Authentification WhatsApp (Meta Cloud API) */}
      <div className="yalola-card p-5 space-y-3" data-testid="int-whatsapp-card">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: "#E7F6EC", color: "#25D366" }}><MessageCircle size={18} /></div>
          <div className="font-bold font-display text-lg">Connexion WhatsApp (Meta Cloud API)</div>
          <span className={`chip text-[10px] ml-auto ${whatsapp.phone_number_id && whatsapp.access_token_set && whatsapp.template_name ? "chip-primary" : ""}`} data-testid="int-whatsapp-status">
            {whatsapp.phone_number_id && whatsapp.access_token_set && whatsapp.template_name ? "Configuré ✓" : "Non configuré"}
          </span>
        </div>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
          Active l'envoi réel des codes WhatsApp pour l'inscription et la connexion sans mot de passe. Sans ces clés, le code n'est pas envoyé (mode dev). Nécessite un template <b>AUTHENTICATION</b> approuvé côté Meta.
          Chaque membre reçoit automatiquement la variante dans <b>sa langue</b> (French → <code>fr</code>, English → <code>en</code>, Spanish → <code>es</code>, Portuguese (POR) → <code>pt_PT</code>) ; langue inconnue → français.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Phone Number ID</Label><Input value={whatsapp.phone_number_id || ""} onChange={(e) => upd("whatsapp", { phone_number_id: e.target.value })} placeholder="1234567890" data-testid="int-whatsapp-phone-id" /></div>
          <div className="flex items-center justify-between rounded-xl px-4 py-2 self-end" style={{ background: "var(--yalola-surface-alt)" }}>
            <span className="text-sm">Activer WhatsApp</span>
            <Switch checked={whatsapp.enabled !== false} onCheckedChange={(v) => upd("whatsapp", { enabled: v })} data-testid="int-whatsapp-enabled" />
          </div>
          <div><Label>Nom du template</Label><Input value={whatsapp.template_name || ""} onChange={(e) => upd("whatsapp", { template_name: e.target.value })} placeholder="yalola_otp" data-testid="int-whatsapp-template" /></div>
          <div><Label>Langue du code de test</Label><Input value={whatsapp.template_language || ""} onChange={(e) => upd("whatsapp", { template_language: e.target.value })} placeholder="fr" data-testid="int-whatsapp-template-lang" /><p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>fr, en, es ou pt (converti automatiquement vers le code Meta, ex. pt → pt_PT). Utilisée uniquement par « Envoyer un code de test » ; les membres reçoivent leur propre langue.</p></div>
          <div><Label>Version API (Graph)</Label><Input value={whatsapp.graph_version || ""} onChange={(e) => upd("whatsapp", { graph_version: e.target.value })} placeholder="v22.0" data-testid="int-whatsapp-graph-version" /></div>
        </div>
        <div>
          <Label>Access Token permanent {whatsapp.access_token_hint && <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>({whatsapp.access_token_hint})</span>}</Label>
          <Input
            type="password"
            value={waAccessToken}
            onChange={(e) => setWaAccessToken(e.target.value)}
            placeholder={whatsapp.access_token_set ? "•••••••• (laisser vide pour garder)" : "EAAG... (token permanent Meta)"}
            data-testid="int-whatsapp-token"
          />
        </div>
        <div className="rounded-xl px-4 py-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="int-whatsapp-test-box">
          <Label>Tester l'envoi (valider les clés en 1 clic)</Label>
          <div className="flex gap-2">
            <Input value={waTestPhone} onChange={(e) => setWaTestPhone(e.target.value)} placeholder="+243812345678" className="flex-1 bg-white" data-testid="int-whatsapp-test-phone" />
            <Button variant="outline" className="rounded-full whitespace-nowrap" onClick={testWhatsApp} disabled={waTesting} data-testid="int-whatsapp-test-btn">
              {waTesting ? "Envoi…" : "Envoyer un code de test"}
            </Button>
          </div>
          {waTestResult && (
            <div className="text-xs font-medium" style={{ color: waTestResult.ok ? "var(--yalola-primary)" : "#DC2626" }} data-testid="int-whatsapp-test-result">
              {waTestResult.ok ? `✓ ${waTestResult.message || "Code envoyé"}` : `✕ ${waTestResult.error || "Échec"}`}
            </div>
          )}
          <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
            Enregistre puis envoie un vrai code WhatsApp à ce numéro pour vérifier que tes clés fonctionnent.
          </p>
        </div>
        <div className="rounded-xl px-4 py-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="int-whatsapp-reminder-box">
          <div className="flex items-center gap-2">
            <Label>Template « rappel avant suppression du compte » (J-3)</Label>
            <span className="chip text-[10px] ml-auto chip-primary" data-testid="int-whatsapp-reminder-status">
              {`Actif ✓ — ${whatsapp.reminder_template_name || "yalola_deletion_reminder"}`}
            </span>
          </div>
          <Input value={whatsapp.reminder_template_name || ""} onChange={(e) => upd("whatsapp", { reminder_template_name: e.target.value })} placeholder="yalola_deletion_reminder (par défaut)" className="bg-white" data-testid="int-whatsapp-reminder-template" />
          <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
            Template <b>Utilitaire</b> approuvé côté Meta, 2 variables dans le corps : <code>{"{{1}}"}</code> = prénom, <code>{"{{2}}"}</code> = jours restants.
            Envoyé une fois, ~3 jours avant la suppression définitive, dans la langue du membre (fr / en / es / pt_PT ; inconnue → français). Laisse vide pour utiliser <code>yalola_deletion_reminder</code>.
            Le même rappel part aussi par e-mail (modèle éditable dans « E-mails »).
          </p>
        </div>

        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
          Les secrets sont stockés de façon sécurisée et jamais réaffichés.
          <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noreferrer" className="underline ml-1" style={{ color: "var(--yalola-primary)" }}>Guide Meta Cloud API →</a>
        </p>
      </div>

      <RevenueCatCard iap={iap} onChange={(patch) => upd("iap", patch)} secret={rcSecret} setSecret={setRcSecret} />

      <div className="flex justify-end"><Button className="pill pill-primary" onClick={save} disabled={saving} data-testid="integrations-save">{saving ? "Enregistrement..." : "Enregistrer"}</Button></div>
    </div>
  );
};
